/**
 * Maps in-memory CPT table rows to the backend CPT update API payload shape.
 * @module transformRowDataForPayload
 */
import moment from "moment";
import { AuthStatusOptionModel } from "../../../../../data-model/authStatusOptions";
import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";
import { getAuthStatusIdFromText } from "../../../../../enums/medicalPaOrdersAuthStatus";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";

/**
 * Converts editable CPT rows into `j_codes` plus `order_id` / `org_id` for the CPT tables update API.
 *
 * @param tableData - Current CPT table model rows
 * @param authStatusOptions - Badge label → status id resolution list
 * @param orderId - Medical PA order identifier
 * @returns Payload accepted by the CPT tables update endpoint
 */
export const transformTableDataToApiPayload = (
  tableData: CptCodesTableRowModel[],
  authStatusOptions: AuthStatusOptionModel[],
  orderId: string,
) => {
  const j_codes = tableData.map((row) => {
    const start_date = row.startFrom?.data
      ? moment(new Date(row.startFrom.data).toISOString()).format("MM/DD/YYYY")
      : "";
    const end_date = row.endAt?.data
      ? moment(new Date(row.endAt.data).toISOString()).format("MM/DD/YYYY")
      : "";

    return {
      id: row.jCodeId || "",
      j_code: row.jCodes.data || "",
      start_date: start_date,
      end_date: end_date,
      num_visits: parseInt(row.visits?.data ?? "0") || 0,
      auth_status:
        getAuthStatusIdFromText(
          row.paRequired?.text,
          authStatusOptions ?? [],
        ) || "",
      medicine_name: row.description.text || "",
      description: row.description.text || "",
      visits_happened: parseInt(row.visitsAllowed?.data ?? "0") || 0,
      visits_remaining: parseInt(row.remainingVisits?.data ?? "0") || 0,
      comment_date: new Date().toISOString(),
      auth_number: row.authorizationNumber?.data || "",
      // `filingInfo.value` is the filing mode; `detail` is the portal name
      // or fax number captured alongside it.
      filing_mode: row.filingInfo?.value || "",
      filing_detail: row.filingInfo?.detail || "",
      pending_number: row.pendingNumber?.data || "",
    };
  });

  return {
    order_id: orderId,
    j_codes: j_codes,
    org_id: getOrgIdForFetchExternalWorklist() || "",
  };
};
